
import React from 'react';
import { Button } from "@/components/ui/button";
import { Play, Clock, Monitor, Globe } from "lucide-react";
import { RrwebSession } from '@/hooks/useRrwebSessions';

interface SessionCardProps {
  session: RrwebSession;
  isSelected?: boolean;
  onSelect?: (session: RrwebSession) => void;
  onPreview?: (session: RrwebSession) => void;
  showActions?: boolean;
}

const SessionCard = ({ 
  session, 
  isSelected = false, 
  onSelect, 
  onPreview, 
  showActions = true 
}: SessionCardProps) => {
  const formatDuration = (ms?: number) => {
    if (!ms) return 'Unknown';
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString([], { 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div 
      className={`bg-editor-medium border rounded-lg p-4 cursor-pointer transition-all hover:bg-editor-light ${
        isSelected ? 'border-editor-accent ring-1 ring-editor-accent' : 'border-editor-light'
      }`}
      onClick={() => onSelect?.(session)}
    >
      {/* Thumbnail placeholder */}
      <div className="aspect-video bg-editor-dark rounded-lg mb-3 flex items-center justify-center">
        <Play className="h-8 w-8 text-gray-400" />
      </div>

      {/* Session Info */}
      <div className="space-y-2">
        <h4 className="font-medium text-white truncate">
          {session.name || `Session ${session.id.slice(0, 8)}`}
        </h4>
        
        {session.description && (
          <p className="text-sm text-gray-400 line-clamp-2">
            {session.description}
          </p>
        )}

        <div className="flex items-center justify-between text-xs text-gray-500">
          <div className="flex items-center space-x-3">
            <div className="flex items-center">
              <Clock className="h-3 w-3 mr-1" />
              {formatDuration(session.duration_ms)}
            </div>
            {session.viewport_width && session.viewport_height && (
              <div className="flex items-center">
                <Monitor className="h-3 w-3 mr-1" />
                {session.viewport_width}×{session.viewport_height}
              </div>
            )}
          </div>
          <div>{formatDate(session.created_at)}</div>
        </div>

        {session.initial_url && (
          <div className="flex items-center text-xs text-gray-500 truncate">
            <Globe className="h-3 w-3 mr-1 flex-shrink-0" />
            <span className="truncate">{session.initial_url}</span>
          </div>
        )}

        {showActions && (
          <div className="flex space-x-2 pt-2">
            <Button
              size="sm"
              variant="ghost"
              onClick={(e) => {
                e.stopPropagation();
                onPreview?.(session);
              }}
              className="flex-1 text-xs"
            >
              Preview
            </Button>
            <Button
              size="sm"
              variant={isSelected ? "default" : "outline"}
              onClick={(e) => {
                e.stopPropagation();
                onSelect?.(session);
              }}
              className="flex-1 text-xs"
            >
              {isSelected ? 'Selected' : 'Select'}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default SessionCard;
